import React from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { TutorialStep } from '../../hooks/useTutorial';
import { getBrandingColor } from '../../utils/brandingStyles';

interface TutorialProps {
  run: boolean;
  steps: TutorialStep[];
  stepIndex: number;
  callback: (data: any) => void;
  continuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  disableOverlayClose?: boolean;
  disableScrolling?: boolean;
}

const Tutorial: React.FC<TutorialProps> = ({
  run,
  steps,
  stepIndex,
  callback,
  continuous = true,
  showProgress = true,
  showSkipButton = true,
  disableOverlayClose = true,
  disableScrolling = false,
}) => {
  const { t } = useTranslation();

  // Intercepter les erreurs react-joyride au niveau global
  React.useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Ignorer les erreurs "Target not mounted" de react-joyride
      const errorMessage = args[0]?.toString() || '';
      if (errorMessage.includes('Target not mounted') || errorMessage.includes('target_not_found')) {
        console.warn('⚠️ Tutorial - Erreur interceptée et ignorée:', errorMessage);
        return;
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  // Fonction pour ouvrir automatiquement les menus lors des étapes correspondantes
  const handleStepChange = React.useCallback((data: any) => {
    try {
      const { action, index, type, status, lifecycle } = data;

      // Gérer l'erreur "Target not mounted"
      if (lifecycle === 'error:target_not_found' || (type === 'error' && lifecycle === 'init')) {
        console.warn('⚠️ Tutorial - Target not mounted, continuing anyway');
        return; // Ignorer l'erreur et continuer
      }

      // Appeler le callback parent
      callback(data);

      // Si on avance vers une nouvelle étape
      if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      const nextStep = steps[index + 1];
      if (nextStep) {
        // Petit délai pour laisser le DOM se mettre à jour
        setTimeout(() => {
          const target = nextStep.target;
          
          // Ouvrir le menu File pour l'export
          if (target === '[data-tutorial="flowchart-export-details"]') {
            const dropdownContainer = document.querySelector('[data-tutorial="flowchart-export-details"]');
            if (dropdownContainer) {
              const dropdownButton = dropdownContainer.querySelector('button[data-bs-toggle="dropdown"]') as HTMLElement;
              if (dropdownButton) {
                // Utiliser Bootstrap API pour ouvrir le dropdown
                const bootstrap = (window as any).bootstrap;
                if (bootstrap && bootstrap.Dropdown) {
                  const dropdown = new bootstrap.Dropdown(dropdownButton);
                  dropdown.show();
                } else {
                  // Fallback: simuler un vrai clic
                  dropdownButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
                }
              }
            }
          }
          
          // Ouvrir le menu View pour les calques
          if (target === '[data-tutorial="flowchart-layers"]') {
            const dropdownContainer = document.querySelector('[data-tutorial="flowchart-layers"]');
            if (dropdownContainer) {
              const dropdownButton = dropdownContainer.querySelector('button[data-bs-toggle="dropdown"]') as HTMLElement;
              if (dropdownButton) {
                const bootstrap = (window as any).bootstrap;
                if (bootstrap && bootstrap.Dropdown) {
                  const dropdown = new bootstrap.Dropdown(dropdownButton);
                  dropdown.show();
                } else {
                  dropdownButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
                }
              }
            }
          }
          
          // Ouvrir le menu Process Owner
          if (target === '[data-tutorial="flowchart-process-owner"]') {
            const dropdownContainer = document.querySelector('[data-tutorial="flowchart-process-owner"]');
            if (dropdownContainer) {
              const dropdownButton = dropdownContainer.querySelector('button[data-bs-toggle="dropdown"]') as HTMLElement;
              if (dropdownButton) {
                const bootstrap = (window as any).bootstrap;
                if (bootstrap && bootstrap.Dropdown) {
                  const dropdown = new bootstrap.Dropdown(dropdownButton);
                  dropdown.show();
                } else {
                  dropdownButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
                }
              }
            }
          }
          
          // Sélectionner une boîte de processus pour montrer les icônes d'attributs
          if (target === '[data-tutorial="flowchart-attributes"]') {
            const processBox = document.querySelector('[data-type="process"]') as HTMLElement;
            if (processBox) {
              processBox.click();
            }
          }
        }, 300);
      }
    }
    } catch (error) {
      console.warn('⚠️ Tutorial - Erreur globale interceptée:', error);
      // Ne pas propager l'erreur, continuer le tutoriel
    }
  }, [callback, steps]);

  const primaryColor = getBrandingColor();
  const joyrideStyles = {
    options: {
      primaryColor: primaryColor,
      backgroundColor: '#1F1F21',
      textColor: '#ffffff',
      overlayColor: 'rgba(0, 0, 0, 0.8)',
      arrowColor: primaryColor,
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: 8,
      fontSize: 14,
      padding: 20,
      backgroundColor: '#1F1F21',
      color: '#ffffff',
      border: `1px solid ${primaryColor}`,
      maxWidth: 800,
    },
    tooltipContainer: {
      textAlign: 'left' as const,
    },
    tooltipTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 10,
      color: primaryColor,
    },
    tooltipContent: {
      fontSize: 14,
      lineHeight: 1.5,
      marginBottom: 15,
      color: '#ffffff',
    },
    tooltipFooter: {
      marginTop: 15,
      textAlign: 'center' as const,
      position: 'relative' as const,
    },
    tooltipFooterSpacer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    buttonNext: {
      backgroundColor: primaryColor,
      color: '#ffffff',
      fontSize: 14,
      fontWeight: 'bold',
      padding: '8px 16px',
      borderRadius: 4,
      border: 'none',
      cursor: 'pointer',
    },
    buttonBack: {
      backgroundColor: 'transparent',
      color: primaryColor,
      fontSize: 14,
      fontWeight: 'bold',
      padding: '8px 16px',
      borderRadius: 4,
      border: `1px solid ${primaryColor}`,
      cursor: 'pointer',
      marginRight: 10,
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      color: '#ffffff',
      fontSize: 14,
      padding: '8px 16px',
      border: '1px solid #ffffff',
      borderRadius: 4,
      cursor: 'pointer',
    },
    buttonClose: {
      backgroundColor: 'transparent',
      color: '#ffffff',
      fontSize: 18,
      padding: 4,
      border: 'none',
      cursor: 'pointer',
      position: 'absolute' as const,
      top: 10,
      right: 10,
    },
    spotlight: {
      borderRadius: 4,
    },
  };

  const locale = {
    back: t('tutorial.joyride.back'),
    close: t('tutorial.joyride.close'),
    last: t('tutorial.joyride.last'),
    next: showProgress && steps.length > 1 
      ? `${t('tutorial.joyride.next')} (${t('tutorial.stepCounter.stepXofY', { 
          step: stepIndex + 1, 
          total: steps.length 
        })})`
      : t('tutorial.joyride.next'),
    open: t('tutorial.joyride.open'),
    skip: t('tutorial.joyride.skip'),
  };

  return (
    <Joyride
      callback={handleStepChange}
      continuous={continuous}
      run={run}
      scrollToFirstStep={false}
      showProgress={false}
      showSkipButton={showSkipButton}
      stepIndex={stepIndex}
      steps={steps}
      disableOverlayClose={disableOverlayClose}
      disableScrolling={false}
      disableCloseOnEsc={true}
      styles={joyrideStyles}
      locale={locale}
      hideCloseButton={false}
      spotlightPadding={0}
      spotlightClicks={false}
      floaterProps={{
        disableAnimation: false,
        hideArrow: false,
        disableFlip: true,
      }}
      debug={false}
    />
  );
};

export default Tutorial;
